import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createTpr,
  getTprById,
  getTprReport,
  listTprByPatient,
  updateTpr,
} from "../../services/tprService";

const initialState = {
  patient: null,
  records: [],
  editingTprId: null,
  loading: false,
  saving: false,
  printing: false,
  error: null,
};

export const fetchTprList = createAsyncThunk(
  "tpr/fetchList",
  async ({ regId, admissionId }, { rejectWithValue }) => {
    try {
      return await listTprByPatient(regId, admissionId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTprById = createAsyncThunk(
  "tpr/fetchById",
  async (tprId, { rejectWithValue }) => {
    try {
      return await getTprById(tprId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveTpr = createAsyncThunk(
  "tpr/save",
  async ({ tprId, payload }, { rejectWithValue }) => {
    try {
      if (tprId) {
        return await updateTpr(tprId, payload);
      }
      return await createTpr(payload);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTprReport = createAsyncThunk(
  "tpr/fetchReport",
  async (admissionId, { rejectWithValue }) => {
    try {
      return await getTprReport(admissionId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const tprSlice = createSlice({
  name: "tpr",
  initialState,
  reducers: {
    setPatient: (state, action) => {
      state.patient = action.payload;
      state.records = [];
      state.editingTprId = null;
    },
    setEditingTprId: (state, action) => {
      state.editingTprId = action.payload;
    },
    clearEditing: (state) => {
      state.editingTprId = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTprList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTprList.fulfilled, (state, action) => {
        state.loading = false;
        state.records = action.payload || [];
      })
      .addCase(fetchTprList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load TPR records";
        state.records = [];
      })
      .addCase(fetchTprById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTprById.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.tprId) {
          state.editingTprId = action.payload.tprId;
        }
      })
      .addCase(fetchTprById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load TPR record";
      })
      .addCase(saveTpr.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveTpr.fulfilled, (state) => {
        state.saving = false;
        state.editingTprId = null;
      })
      .addCase(saveTpr.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to save TPR record";
      })
      .addCase(fetchTprReport.pending, (state) => {
        state.printing = true;
        state.error = null;
      })
      .addCase(fetchTprReport.fulfilled, (state) => {
        state.printing = false;
      })
      .addCase(fetchTprReport.rejected, (state, action) => {
        state.printing = false;
        state.error = action.payload || "Failed to load TPR report";
      });
  },
});

export const { setPatient, setEditingTprId, clearEditing, clearError } =
  tprSlice.actions;

export default tprSlice.reducer;
